/**
 * The persisted ``lambda_value`` is the fitted log-log exponent ``m`` in
 * Pc = Pe · Swn^m. For a drainage curve m is negative. Zone-value
 * workflows may instead persist its positive magnitude (−m). Classic
 * Brooks-Corey literature instead calls λBC the positive pore-size
 * distribution index in Pc = Pe · Swn^(-1/λBC), hence λBC = 1/|m|.
 *
 * These helpers are presentation-only. They intentionally do not change the
 * stored value, curve reconstruction, or correlation contract.
 */
export const BROOKS_COREY_EXPONENT_LABEL =
  'Fitted capillary-pressure exponent (m)';

export const BROOKS_COREY_PORE_SIZE_INDEX_LABEL =
  'Brooks-Corey pore-size distribution index (λBC)';

export const toBrooksCoreyPoreSizeDistributionIndex = (
  exponent?: number | null
): number | null => {
  if (
    typeof exponent !== 'number' ||
    !Number.isFinite(exponent) ||
    exponent === 0
  ) {
    return null;
  }

  const index = 1 / Math.abs(exponent);
  return Number.isFinite(index) && index > 0 ? index : null;
};

/** Presentation-only precision used by the other fitted Pc parameters. */
export const roundBrooksCoreyPoreSizeDistributionIndex = (
  exponent?: number | null
): number | null => {
  const index = toBrooksCoreyPoreSizeDistributionIndex(exponent);
  return index === null ? null : Number(index.toFixed(4));
};

export const roundBrooksCoreyDisplayValue = (
  value?: number | null
): number | null =>
  typeof value === 'number' && Number.isFinite(value)
    ? Number(value.toFixed(4))
    : null;
